import React, { useState, useEffect } from 'react';
import { Mail, Eye, Tag } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface EmailStepConfigProps {
  config: any;
  onChange: (config: any) => void;
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  html_content: string;
  text_content?: string;
}

const MERGE_TAGS = [
  { tag: '{{first_name}}', label: 'First Name' },
  { tag: '{{last_name}}', label: 'Last Name' },
  { tag: '{{email}}', label: 'Email' },
  { tag: '{{company_name}}', label: 'Company Name' },
  { tag: '{{phone}}', label: 'Phone' },
  { tag: '{{address}}', label: 'Address' },
  { tag: '{{city}}', label: 'City' },
  { tag: '{{state}}', label: 'State' },
  { tag: '{{zip}}', label: 'ZIP Code' },
  { tag: '{{service_type}}', label: 'Service Type' },
  { tag: '{{agreement_status}}', label: 'Agreement Status' },
  { tag: '{{unsubscribe_link}}', label: 'Unsubscribe Link' },
];

export const EmailStepConfig: React.FC<EmailStepConfigProps> = ({ config, onChange }) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    if (config.template_id && templates.length > 0) {
      const template = templates.find(t => t.id === config.template_id);
      setSelectedTemplate(template || null);
      // If subject is not already set, populate from template
      if (template && !config.subject) {
        onChange({
          ...config,
          subject: template.subject || '',
        });
      }
    }
  }, [config.template_id, templates]);

  const loadTemplates = async () => {
    try {
      // Query from email_templates table (the actual table used by the system)
      const { data, error } = await supabase
        .from('email_templates')
        .select('id, name, subject, body')
        .order('name');

      if (error) throw error;
      // Map 'body' field to 'html_content' for compatibility with preview
      const mappedData = (data || []).map(t => ({
        ...t,
        html_content: t.body
      }));
      setTemplates(mappedData);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    onChange({
      ...config,
      template_id: templateId,
      subject: template?.subject || config.subject || '',
    });
  };

  const insertMergeTag = (tag: string, field: 'subject' | 'preview_text') => {
    const currentValue = config[field] || '';
    onChange({
      ...config,
      [field]: currentValue + tag,
    });
  };

  const renderPreview = () => {
    if (!selectedTemplate) return null;

    const previewHtml = selectedTemplate.html_content
      .replace(/{{first_name}}/g, 'John')
      .replace(/{{last_name}}/g, 'Doe')
      .replace(/{{email}}/g, 'john.doe@example.com')
      .replace(/{{company_name}}/g, 'Example Company')
      .replace(/{{phone}}/g, '(555) 123-4567')
      .replace(/{{service_type}}/g, 'HVAC Maintenance')
      .replace(/{{agreement_status}}/g, 'Active');

    return (
      <div className="border rounded-lg p-4 bg-white">
        <div className="mb-4 pb-4 border-b">
          <div className="text-sm text-gray-500">Subject:</div>
          <div className="font-medium">{config.subject || selectedTemplate.subject}</div>
          {config.preview_text && (
            <>
              <div className="text-sm text-gray-500 mt-2">Preview Text:</div>
              <div className="text-sm">{config.preview_text}</div>
            </>
          )}
        </div>
        <div 
          className="prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: previewHtml }}
        />
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Template Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Email Template *
        </label>
        {loading ? (
          <div className="text-sm text-gray-500">Loading templates...</div>
        ) : (
          <select
            value={config.template_id || ''}
            onChange={(e) => handleTemplateChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select a template...</option>
            {templates.map(template => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
        )}
        {templates.length === 0 && !loading && (
          <p className="mt-2 text-sm text-amber-600">
            No email templates found. Create templates in the Templates page first.
          </p>
        )}
      </div>

      {/* Subject Line */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Subject Line *
          </label>
          <div className="flex gap-1">
            {MERGE_TAGS.slice(0, 3).map(({ tag, label }) => (
              <button
                key={tag}
                type="button"
                onClick={() => insertMergeTag(tag, 'subject')}
                className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                title={`Insert ${label}`}
              >
                <Tag className="w-3 h-3" />
              </button>
            ))}
          </div>
        </div>
        <input
          type="text"
          value={config.subject || ''}
          onChange={(e) => onChange({ ...config, subject: e.target.value })}
          placeholder="Enter email subject..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <p className="mt-1 text-xs text-gray-500">
          Use merge tags like {MERGE_TAGS.slice(0, 2).map(t => t.tag).join(', ')}
        </p>
      </div>

      {/* Preview Text */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Preview Text (Preheader)
          </label>
          <div className="flex gap-1">
            {MERGE_TAGS.slice(0, 3).map(({ tag, label }) => (
              <button
                key={tag}
                type="button"
                onClick={() => insertMergeTag(tag, 'preview_text')}
                className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                title={`Insert ${label}`}
              >
                <Tag className="w-3 h-3" />
              </button>
            ))}
          </div>
        </div>
        <input
          type="text"
          value={config.preview_text || ''}
          onChange={(e) => onChange({ ...config, preview_text: e.target.value })}
          placeholder="Text shown in inbox preview..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          maxLength={150}
        />
        <p className="mt-1 text-xs text-gray-500">
          {(config.preview_text || '').length}/150 characters
        </p>
      </div>

      {/* From Name & Email */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            From Name
          </label>
          <input
            type="text"
            value={config.from_name || ''}
            onChange={(e) => onChange({ ...config, from_name: e.target.value })}
            placeholder="Company Name"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            From Email
          </label>
          <input
            type="email"
            value={config.from_email || ''}
            onChange={(e) => onChange({ ...config, from_email: e.target.value })}
            placeholder="noreply@example.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Reply-To Email */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Reply-To Email
        </label>
        <input
          type="email"
          value={config.reply_to || ''}
          onChange={(e) => onChange({ ...config, reply_to: e.target.value })}
          placeholder="support@example.com"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Email Settings */}
      <div className="space-y-3">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.track_opens || false}
            onChange={(e) => onChange({ ...config, track_opens: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700">Track email opens</span>
        </label>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.track_clicks || false}
            onChange={(e) => onChange({ ...config, track_clicks: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700">Track link clicks</span>
        </label>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.include_unsubscribe || true}
            onChange={(e) => onChange({ ...config, include_unsubscribe: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700">Include unsubscribe link</span>
        </label>
      </div>

      {/* Preview Button */}
      {selectedTemplate && (
        <div>
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            <Eye className="w-4 h-4" />
            {showPreview ? 'Hide' : 'Show'} Preview
          </button>
        </div>
      )}

      {/* Preview */}
      {showPreview && selectedTemplate && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Email Preview</h4>
          {renderPreview()}
        </div>
      )}

      {/* Available Merge Tags */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Available Merge Tags</h4>
        <div className="grid grid-cols-2 gap-2">
          {MERGE_TAGS.map(({ tag, label }) => (
            <div key={tag} className="text-xs">
              <code className="bg-white px-2 py-1 rounded border border-gray-200">
                {tag}
              </code>
              <span className="ml-2 text-gray-600">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
